import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [location]);

  // Lock body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <>
      <style>{`
        .nav-root {
          position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
          height: var(--nav-h);
          background: ${scrolled ? 'rgba(8,8,16,0.97)' : 'rgba(8,8,16,0.6)'};
          backdrop-filter: blur(16px);
          border-bottom: 1px solid ${scrolled ? 'rgba(255,107,0,0.12)' : 'transparent'};
          transition: background 0.3s, border-color 0.3s;
        }
        .nav-inner {
          max-width: 1180px; margin: 0 auto; padding: 0 20px;
          height: 100%; display: flex; align-items: center; justify-content: space-between;
        }
        .nav-logo { text-decoration: none; display: flex; align-items: center; gap: 10px; }
        .nav-logo-icon {
          width: 36px; height: 36px; border-radius: 9px;
          background: linear-gradient(135deg, #ff6b00, #ff9500);
          display: flex; align-items: center; justify-content: center; font-size: 17px;
        }
        .nav-logo-text {
          font-family: 'Bebas Neue', sans-serif; font-size: 22px;
          letter-spacing: 0.08em; color: #fff;
        }
        .nav-logo-text span { color: #ff6b00; }

        .nav-links { display: flex; align-items: center; gap: 28px; }
        .nav-link {
          font-family: 'Rajdhani', sans-serif; font-weight: 600; font-size: 15px;
          letter-spacing: 0.07em; text-transform: uppercase; text-decoration: none;
          color: rgba(255,255,255,0.65); transition: color 0.2s; position: relative; padding-bottom: 2px;
        }
        .nav-link:hover { color: #fff; }
        .nav-link.active { color: #ff6b00; }
        .nav-link.active::after {
          content: ''; position: absolute; bottom: -2px; left: 0; right: 0;
          height: 2px; background: #ff6b00; border-radius: 1px;
        }

        .nav-auth { display: flex; align-items: center; gap: 10px; }
        .nav-user {
          display: flex; align-items: center; gap: 9px;
          padding: 6px 12px; background: rgba(255,107,0,0.08);
          border: 1px solid rgba(255,107,0,0.18); border-radius: 8px;
        }
        .nav-avatar {
          width: 28px; height: 28px; border-radius: '50%';
          background: linear-gradient(135deg, #ff6b00, #ff9500);
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 700; color: #fff; border-radius: 50%;
          flex-shrink: 0;
        }
        .nav-username {
          font-family: 'Rajdhani', sans-serif; font-weight: 600; font-size: 14px; color: #fff;
        }

        /* Hamburger */
        .hamburger {
          display: none; flex-direction: column; justify-content: center;
          gap: 5px; width: 38px; height: 38px; cursor: pointer;
          background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.08);
          border-radius: 8px; padding: 8px; transition: background 0.2s;
        }
        .hamburger:hover { background: rgba(255,255,255,0.1); }
        .hamburger span {
          display: block; height: 2px; background: #fff; border-radius: 1px;
          transition: all 0.25s ease; transform-origin: center;
        }
        .hamburger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
        .hamburger.open span:nth-child(2) { opacity: 0; transform: scaleX(0); }
        .hamburger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

        /* Mobile menu */
        .mobile-menu {
          display: none; position: fixed; top: var(--nav-h); left: 0; right: 0; bottom: 0;
          background: rgba(8,8,16,0.98); backdrop-filter: blur(20px);
          z-index: 999; flex-direction: column; padding: 28px 20px;
          animation: fadeIn 0.2s ease;
        }
        .mobile-menu.open { display: flex; }
        .mobile-nav-link {
          font-family: 'Bebas Neue', sans-serif; font-size: 32px;
          letter-spacing: 0.06em; text-decoration: none;
          color: rgba(255,255,255,0.7); padding: 14px 0;
          border-bottom: 1px solid rgba(255,255,255,0.06);
          transition: color 0.2s;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active { color: #ff6b00; }
        .mobile-auth { margin-top: 28px; display: flex; flex-direction: column; gap: 10px; }

        @media (max-width: 768px) {
          .nav-links { display: none !important; }
          .nav-auth .btn-ghost { display: none; }
          .nav-auth .nav-user .nav-username { display: none; }
          .hamburger { display: flex; }
        }
        @media (max-width: 480px) {
          .nav-inner { padding: 0 14px; }
        }
      `}</style>

      <nav className="nav-root">
        <div className="nav-inner">
          {/* Logo */}
          <Link to="/" className="nav-logo">
            <div className="nav-logo-icon">🏏</div>
            <span className="nav-logo-text">IPL <span>TICKETS</span></span>
          </Link>

          {/* Desktop links */}
          <div className="nav-links">
            <NavLink to="/matches">Matches</NavLink>
            {user && <NavLink to="/my-bookings">My Tickets</NavLink>}
          </div>

          {/* Auth + Hamburger */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="nav-auth">
              {user ? (
                <>
                  <div className="nav-user">
                    <div className="nav-avatar">{user.name?.charAt(0).toUpperCase()}</div>
                    <span className="nav-username">{user.name?.split(' ')[0]}</span>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={handleLogout} style={{ fontSize: 13 }}>
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn btn-ghost btn-sm">Login</Link>
                  <Link to="/register" className="btn btn-primary btn-sm">Sign Up</Link>
                </>
              )}
            </div>
            <button className={`hamburger ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(v => !v)}>
              <span /><span /><span />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
        <MobileNavLink to="/matches" active={location.pathname.startsWith('/matches')}>Matches</MobileNavLink>
        {user && <MobileNavLink to="/my-bookings" active={location.pathname === '/my-bookings'}>My Tickets</MobileNavLink>}

        <div className="mobile-auth">
          {user ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="nav-avatar" style={{ width: 40, height: 40, fontSize: 16 }}>{user.name?.charAt(0).toUpperCase()}</div>
                <div>
                  <div style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: 18, color: '#fff' }}>{user.name}</div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>{user.email}</div>
                </div>
              </div>
              <button className="btn btn-ghost" onClick={handleLogout} style={{ marginTop: 8 }}>Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-ghost">Login</Link>
              <Link to="/register" className="btn btn-primary">Sign Up Free</Link>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function NavLink({ to, children }) {
  const { pathname } = useLocation();
  const active = pathname === to || pathname.startsWith(to + '/');
  return <Link to={to} className={`nav-link ${active ? 'active' : ''}`}>{children}</Link>;
}

function MobileNavLink({ to, children, active }) {
  return <Link to={to} className={`mobile-nav-link ${active ? 'active' : ''}`}>{children}</Link>;
}